import {
  Home,
  CheckSquare,
  FileText,
  Users,
  Calendar,
  Settings,
  HelpCircle,
  Stethoscope,
  Megaphone,
} from 'lucide-react';
import type { NavItemDef, NavSection } from './sidebar-primitives';
import { NAV_KEY, navGroup, navItem } from '@/i18n/keys/nav';

/**
 * The parent nav, as data (design grammar §4.3).
 *
 * Extracted from `Sidebar.tsx` on the shape `trfer-nav.ts` already uses. The
 * reason is the one #611 proved on the TRFer side: while the list lived inside
 * the component, `HelpPage` kept a hand-written second copy of it, and the two
 * drifted until help described a nav that no longer existed. Nothing could
 * catch that, because two hand-maintained lists cannot be diffed by review.
 *
 * Now the list lives here, the sidebar renders it, the help page derives its
 * cards from it, and `HelpPage.navParity.test.tsx` fails the build in both
 * directions.
 *
 * Group order and membership are a product decision, recorded once in
 * DECISIONS (2026-08-21, the #844 sidebar wave) and restated in design grammar
 * §4.3. Changing either is a DECISIONS entry, not a PR description.
 */

/**
 * Footer utilities, rendered outside the grouped nav by `Sidebar` itself.
 *
 * ONE tray, shared by the parent and admin shells, because `Sidebar` renders
 * one footer for both roles — the tray belongs to the shell, not to a role, so
 * `admin-nav.ts` deliberately does not declare a second copy of it. Exported
 * so the parity test sees the complete surface list for either role.
 *
 * Settings earns a help card; Help does not — a help entry describing the help
 * page is a self-reference, and the parity test exempts it by href.
 *
 * Labels resolve through `NAV_KEY` when read (#2109 L6), so this module-level
 * tray follows a language change without becoming a function.
 */
export const SIDEBAR_FOOTER_ITEMS: NavItemDef[] = [
  navItem(NAV_KEY.settings, '/settings', Settings),
  navItem(NAV_KEY.help,     '/help',     HelpCircle),
];

export interface ParentNavState {
  /**
   * The memorial gate (#693). The /medical route renders `HealthPage`, which
   * for a bereaved family is a memorial view rather than the care hub — but a
   * standing nav item is a daily invitation, and the invitation is the defect.
   * The route stays reachable and stays safe (a bookmark lands on the memorial
   * view); only the offer goes.
   */
  showHealth: boolean;
}

/**
 * Build the parent nav for one reader.
 *
 * Grouped sections matching the design's sidebar taxonomy. The order within
 * each group reflects how caregivers actually reach for things (#844): a
 * label-less home group (a single obvious item needs no eyebrow), the
 * medical/care workload with the care hub first, and the people-side network.
 * Settings + Help live in a label-less utility tray below all groups
 * (`SIDEBAR_FOOTER_ITEMS`).
 *
 * The companion has no nav entry (#844): the right-rail widget and the
 * dashboard card already offer it on the same screen, and three entry points
 * were two too many. Mobile keeps its `BottomNav` Chat slot — the drawer nav
 * is secondary there, so the asymmetry is deliberate.
 *
 * Gating is PER ITEM, never per group (design grammar §4.3): sections whose
 * items were all gated away are dropped so an empty label cannot render.
 */
export function buildParentNavSections({ showHealth }: ParentNavState): NavSection[] {
  return [
    {
      // `end` is DATA, declared on the item and consumed by the renderer —
      // never recomputed per href inside the component (grammar §4.3).
      items: [
        // "Today", not "Home" — ratified DECISIONS 2026-08-26, design grammar
        // §4.4. The eyebrow on `DashboardPage` already said it, the native
        // TRFer shell already labelled it, and for a family in treatment
        // "Today" is the one word that says the page is time-scoped; "Home"
        // reads as "the app". The HREF and the `dashboard` menu key are
        // unchanged — the key is persisted in live `allowed_menus` grants.
        navItem(NAV_KEY.today, '/dashboard', Home, { end: true }),
        // Conversations left this group on 2026-09-21 (Victor: "Move the
        // /conversations to settings"; spec
        // 2026-09-21-parent-chrome-and-grants-design.md Part 2). The archive
        // is a section of Settings › Companion, and the quick reach is the
        // history icon on the Today composer — so the thread list kept its
        // two doors and gave back its nav row. `/conversations` redirects,
        // and the persisted `chat` menu key is unchanged: labels rename,
        // keys never (§2.8).
      ],
    },
    navGroup(NAV_KEY.groupCare, [
        // Renewal surfacing (08-19 call) does NOT add a nav entry here: Mario's
        // 2026-08-21 walk ruled grants belong under Finances, as its prominent
        // second section (/bills?tab=grants) rather than a sixth Care
        // destination. Renewal prominence is carried by the Grants section
        // itself (its renewal status line) plus the seasonal Home banner — the
        // in-season chip the tab once carried left with the 2026-09-10
        // declutter (no chip in a tab label, design-grammar §4.5).
        // "Health", not "Medical": the page became HealthPage in 35601a57 and
        // the nav label was left behind. The care hub is not the retired
        // medical-bill lane (#641), and one destination gets one noun
        // (design-grammar §4 — the eyebrow is matched to the nav label, so this
        // is the one that has to be right first).
        ...(showHealth ? [navItem(NAV_KEY.health, '/medical', Stethoscope)] : []),
        // "Grants", not "Finances" (2026-09-21). Victor, on the page: "Are we
        // managing/applying for grants? Is it a dashboard?" — "Finances"
        // promised household budgeting and delivered a grant list plus a
        // reimbursement ledger. A row is named for what the reader DOES there
        // (§2.8 rule 2), and the reader manages TRF grants. The href and the
        // persisted `bills` menu key are unchanged: labels rename, keys never.
        navItem(NAV_KEY.grants,   '/bills',    FileText),
        navItem(NAV_KEY.tasks,    '/tasks',    CheckSquare),
        navItem(NAV_KEY.calendar, '/calendar', Calendar),
    ]),
    // Group label "From TRF" since wave w1023c (Victor, 2026-10-23; was
    // "Network"): both rows are things TRF sends the family. The key
    // `groups.network` is unchanged — labels rename, keys never.
    navGroup(NAV_KEY.groupNetwork, [
        navItem(NAV_KEY.announcements, '/community', Megaphone),
        // "Support" from 2026-09-18 (DECISIONS "Subtraction in the chrome").
        // "Resources & contacts" for an hour on 2026-10-23 (DECISIONS "Community
        // rows renamed; Threads removed"), then "Resources" once the People
        // address book left (wave w1023c L2): the page is the family's TRF
        // contact and the resources TRF matched to them. "Support" promised a
        // service TRF does not provide. Href and the `circle` menu key unchanged.
        navItem(NAV_KEY.support, '/circle', Users),
    ]),
  ].filter((s) => s.items.length > 0);
}

/**
 * Map parent sidebar hrefs to impersonation menu keys.
 *
 * Co-located with the nav data it filters (design grammar §4.3: "one
 * `MENU_KEY_MAP` per role, co-located with that role's nav data"). It moved
 * here out of `Sidebar.tsx` with the extraction; the KEYS are unchanged,
 * because each one is persisted in existing `allowed_menus` grants and a
 * rename would silently revoke the item from every live grant.
 *
 * No '/chat' entry: the companion nav item left the sidebar in #844, so the
 * map filters nothing at that href. The `chat` key itself stays in the
 * impersonation registry (`lib/impersonationMenus.ts`) — it is persisted in
 * existing grants and the /chat route remains reachable. `/conversations`
 * left this map on 2026-09-21 with its nav row: the route is now a redirect
 * into Settings › Companion, and a redirect has nothing to filter. The
 * `chat` key itself is untouched, because it is persisted in live grants.
 *
 * Lockstep contract: the filter fails OPEN for unmapped hrefs and CLOSED for
 * mapped keys missing from `allowed_menus`, so every key here MUST have a
 * label in `PARENT_MENU_LABELS`. Pinned by `TRFerSidebar.test.tsx`.
 */
export const MENU_KEY_MAP: Record<string, string> = {
  '/community': 'community',
  '/circle': 'circle',
  '/tasks': 'tasks',
  '/calendar': 'calendar',
  '/medical': 'medical',
  // Grants ride the `bills` key: they are a tab of Finances (/bills?tab=grants),
  // not a destination of their own, so one persisted key still governs the
  // whole money surface under impersonation.
  '/bills': 'bills',
  '/settings': 'settings',
  '/dashboard': 'dashboard',
};
